import { useBounds } from "@react-three/drei";
import { useThree } from "@react-three/fiber";
import JSZip from "jszip";
import { forwardRef, useEffect, useState } from "react";
import { Box3, FrontSide, Vector3 } from "three";
import { MTLLoader } from "three/examples/jsm/loaders/MTLLoader.js";
import { OBJLoader } from "three/examples/jsm/loaders/OBJLoader.js";
import { centralizeControls, centralizeModel, initializeRotation } from "../ModelPreview";

const ModelOBJZip = forwardRef(({ url, data, adjustScale, rotation, onLoad, setAxisThickness, onScreenshot, intl }, ref) => {
  const [urlObj, setUrlObj] = useState(null);
  const [urlMat, setUrlMat] = useState(null);
  const [model, setModel] = useState(null);

  const { meshRef, controlRef, sceneRef, lineRef } = ref;
  const { gl, scene } = useThree();
  const bounds = useBounds();

  /**
   * Set the sceneRef.
   */
  useEffect(() => {
    if (scene) {
      sceneRef.current = scene;
    } else {
      sceneRef.current = null;
    }
  }, [scene]);

  /**
   * Enables / disables loading-screen.
   */
  useEffect(() => {
    if (model) {
      // disable loading-screen
      onLoad(true);
    } else {
      // enable loading-screen
      onLoad(false);
    }
  }, [model]);

  /**
   * Takes a screenshot of the current model.
   */
  useEffect(() => {
    if (onScreenshot && model && gl) {
      onScreenshot(gl);
    }
  }, [model, gl]);

  /**
   * Prepare the materials for viewing.
   */
  useEffect(() => {
    if (model) {
      model.renderOrder = 500;
      const lightEl = [];
      model.traverse(child => {
        if (child.isLight) {
          lightEl.push(child);
        } else if (child.isMesh) {
          const childMesh = child;
          childMesh.renderOrder = 500;
          childMesh.castShadow = true;
          childMesh.receiveShadow = false;

          const materials = Array.isArray(childMesh.material)
            ? childMesh.material
            : [childMesh.material];
          materials.forEach((material) => {
            material.depthWrite = true;
            material.depthTest = true;
            material.side = FrontSide;
            material.shadowSide = FrontSide;
          });
        }
      });
      lightEl.forEach(el => {
        el.parent?.remove(el);
      });
    }
  }, [model]);

  useEffect(() => {
    // fetch the zip-file, unzip it and check the files for support
    const fetchAndUnzip = async () => {
      try {
        const response = await fetch(url);
        const blob = await response.blob();
        const zip = await JSZip.loadAsync(blob);

        let objFound = false;
        setModel(null);
        for (const relativePath of Object.keys(zip.files)) {
          const file = zip.files[relativePath];
          const fileBlob = await file.async('blob');
          if (relativePath.endsWith('.obj')) {
            objFound = true;
            setUrlObj(URL.createObjectURL(fileBlob));
          } else if (relativePath.endsWith('.mtl')) {
            setUrlMat(URL.createObjectURL(fileBlob));
          }
        }
        if (!objFound) {
          // if no .obj-file was found, set model to null and do not display anything
          setUrlObj(null);
          setUrlMat(null);
          setModel(null);
        }
      } catch (error) {
        console.error("Error fetching or unzipping the file:", error);
      }
    };
    fetchAndUnzip();
  }, [url])

  useEffect(() => {
    // load the .obj and .mtl file
    if (urlObj) {
      const loader = new OBJLoader();
      if (urlMat) {
        const mtlLoader = new MTLLoader();
        mtlLoader.load(urlMat, mat => {
          mat.preload();
          loader.setMaterials(mat);
          loader.load(urlObj, obj => {
            setModel(obj);
          });
        });
      } else {
        loader.load(urlObj, obj => {
          setModel(obj);
        });
      }
    }
  }, [urlMat, urlObj]);

  /**
     * Returns the maximum coordinate of the model.
     * @param {*} model 
     * @returns 
     */
  const getMaximumCoordinate = (model) => {
    const box3 = new Box3().setFromObject(model);
    const size = new Vector3();
    box3.getSize(size);
    return Math.max(size.x, size.y, size.z);
  };

  /**
   * Centralize the model in the beginning.
   */
  useEffect(() => {
    initializeRotation(model, rotation);
    centralizeModel(model, lineRef, scene, data, intl);
    centralizeControls(controlRef, bounds, gl);
    // update axis-thickness
    if (setAxisThickness && model) {
      const maximum = getMaximumCoordinate(model);
      setAxisThickness(maximum * 0.004);
    }
  }, [model, gl, scene, bounds, data.scale]);

  useEffect(() => {
    if (!model) return;
    const maximum = getMaximumCoordinate(model);
    if (maximum > 50) {
      // adjust the scale according to maximum coordinate
      adjustScale(maximum);
    }
  }, [model]);

  return model ? (
    <primitive
      ref={meshRef}
      object={model}
      scale={(data.scale === undefined || data.scale === 0) ? 1.0 : data.scale}
    />
  ) : null;
});

export default ModelOBJZip;